import React from "react";
import { Navigate, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { errorMessage, isPlaceholderEmail, type EmailStatus } from "@atlas/shared";
import { useAuth } from "../../hooks/useAuth";
import { EMAIL_STATUS_QUERY_KEY, fetchEmailStatus, requestEmailChange } from "../../api/account";
import { Button } from "../../components/Button";
import { telemetry } from "../../lib/telemetry";

// ─── One-off "add your email" step after a Strava-first signup ──────────────
// Strava never shares an address, so a Strava-first account starts on a
// placeholder and Atlas cannot reach it. The Strava callback sends a brand-new
// web signup to /dashboard?welcome=email, which redirects here (router.tsx).
// Skippable; the dashboard EmailCaptureCard stays as the fallback. Same call
// as that card (POST /account/email sends the confirmation link).
//
// "Once" = the redirect param only ever comes from the callback that created
// the account, plus a per-user localStorage flag so a refresh or the back
// button does not bring it round again. A real (or verified) address, or the
// flag, sends the visitor straight on to the dashboard.

const SEEN_PREFIX = "atlas-welcome-email-seen:";

export function hasSeenWelcomeEmail(userId: string): boolean {
  try {
    return localStorage.getItem(SEEN_PREFIX + userId) !== null;
  } catch {
    return false;
  }
}

function markWelcomeEmailSeen(userId: string): void {
  try {
    localStorage.setItem(SEEN_PREFIX + userId, "1");
  } catch {
    // best-effort
  }
}

const cardStyle: React.CSSProperties = {
  background: "var(--surface-card)",
  border: "1px solid var(--border-subtle)",
  borderRadius: "var(--radius-xl)",
  padding: "var(--space-8)",
  boxShadow: "var(--shadow-md), var(--ring-top)",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  height: "var(--control-md)",
  padding: "0 12px",
  background: "var(--surface-sunken)",
  border: "1px solid var(--border-default)",
  borderRadius: "var(--radius-md)",
  color: "var(--text-primary)",
  fontSize: "var(--text-base)",
  fontFamily: "var(--font-sans)",
  outline: "none",
  boxSizing: "border-box",
};

const labelStyle: React.CSSProperties = {
  fontSize: "var(--text-sm)",
  fontWeight: 600,
  color: "var(--text-secondary)",
  fontFamily: "var(--font-sans)",
};

export default function WelcomeEmailPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");

  // Decided on first render so marking the flag below cannot flip it mid-visit.
  const [eligible] = React.useState(() => !!user && isPlaceholderEmail(user.email) && !hasSeenWelcomeEmail(user.id));

  React.useEffect(() => {
    if (!user || !eligible) return;
    markWelcomeEmailSeen(user.id);
    telemetry.track("action", "welcome_email_shown");
  }, [user, eligible]);

  const statusQuery = useQuery({
    queryKey: EMAIL_STATUS_QUERY_KEY,
    queryFn: fetchEmailStatus,
    enabled: eligible,
    staleTime: 15_000,
  });

  const changeMutation = useMutation({
    mutationFn: requestEmailChange,
    onSuccess: (res) => {
      setPassword("");
      telemetry.track("action", "welcome_email_submitted");
      // The dashboard card reads this key, so it shows "Check your inbox"
      // rather than the form again.
      queryClient.setQueryData<EmailStatus>(EMAIL_STATUS_QUERY_KEY, (prev) =>
        prev ? { ...prev, pending: res.pending } : prev,
      );
      void queryClient.invalidateQueries({ queryKey: EMAIL_STATUS_QUERY_KEY });
    },
  });

  if (!user || !eligible) return <Navigate to="/dashboard" replace />;

  const toDashboard = () => void navigate({ to: "/dashboard", replace: true });
  const pending = changeMutation.data?.pending ?? null;
  const requiresPassword = statusQuery.data?.requiresPassword ?? user.hasPassword;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    changeMutation.mutate({ email: trimmed, ...(requiresPassword ? { password } : {}) });
  }

  if (pending) {
    // Same wording as the EmailCaptureCard's pending state.
    return (
      <div style={cardStyle}>
        <h1 style={{ font: "var(--type-h2)", color: "var(--text-primary)" }}>Check your inbox</h1>
        <p style={{ fontSize: 14, color: "var(--text-secondary)", marginTop: 8, lineHeight: 1.55 }}>
          We sent a link to {pending.email}. Click it and you're done. Not there? Check spam, and note that an
          address already registered to another Atlas account won't receive it.
        </p>
        <div style={{ marginTop: 24 }}>
          <Button variant="primary" block onClick={toDashboard}>
            Continue
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      <h1 style={{ font: "var(--type-h2)", color: "var(--text-primary)" }}>
        Where should we send your walk summaries?
      </h1>
      <p style={{ fontSize: 14, color: "var(--text-secondary)", marginTop: 8, lineHeight: 1.55 }}>
        Strava doesn't share your email with us. Add one so you hear about new achievements, and if anything
        with your account needs fixing.
      </p>

      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 24 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label htmlFor="welcome-email" style={labelStyle}>
            Email
          </label>
          <input
            id="welcome-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            autoFocus
            required
            style={inputStyle}
          />
        </div>
        {requiresPassword && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label htmlFor="welcome-password" style={labelStyle}>
              Current password
            </label>
            <input
              id="welcome-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              style={inputStyle}
            />
          </div>
        )}
        {changeMutation.isError && (
          <p role="alert" style={{ fontSize: 13, color: "var(--status-danger)", margin: 0 }}>
            {errorMessage(changeMutation.error, "Couldn't send the link. Try again.")}
          </p>
        )}
        <Button
          type="submit"
          variant="primary"
          block
          disabled={changeMutation.isPending || !email.trim() || (requiresPassword && !password)}
        >
          {changeMutation.isPending ? "Sending…" : "Add email"}
        </Button>
        <Button
          type="button"
          variant="ghost"
          block
          disabled={changeMutation.isPending}
          onClick={() => {
            telemetry.track("action", "welcome_email_skipped");
            toDashboard();
          }}
        >
          Not now
        </Button>
      </form>
    </div>
  );
}
